import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { useSeo } from '@/hooks/useSeo';

const SITE_URL = 'https://luosai123.cn';
const FORM_ENDPOINT = 'https://readdy.ai/api/form/dah8pkroh653ivfvnoig';
const MESSAGE_LIMIT = 500;

const feedbackTypes = [
  '入口失效或改版',
  '补充新的平台 / 社区',
  '内容有误需要更正',
  '不确定该投哪里，想请人帮忙看看',
  '合作与内容共建',
];

const infoCards = [
  {
    icon: 'ri-time-line',
    title: '回复时效',
    desc: '我们通常在 2 至 3 个工作日内查看并回复，涉及入口核验的反馈会稍慢一些。',
  },
  {
    icon: 'ri-mail-send-line',
    title: '邮件联系',
    desc: '如果不方便填写表单，也可以直接发送邮件到 hello@baoliao-online.example.com。',
  },
  {
    icon: 'ri-shield-line',
    title: '隐私说明',
    desc: '你填写的信息仅用于回复本次反馈，不会被公开，也不会用于任何营销用途。',
  },
];

export default function ContactPage() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [messageLength, setMessageLength] = useState(0);

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: '联系我们',
        url: `${SITE_URL}/contact`,
        description: '提交入口失效反馈、补充新的爆料平台或就投稿方向进行咨询。',
        inLanguage: 'zh-CN',
        mainEntity: {
          '@type': 'Organization',
          name: '爆料网上',
          url: `${SITE_URL}/`,
          email: 'hello@baoliao-online.example.com',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: '联系我们', item: `${SITE_URL}/contact` },
        ],
      },
    ],
    [],
  );

  useSeo({
    title: '联系我们 - 反馈入口失效与补充渠道｜爆料网上',
    description:
      '通过爆料网上联系我们页面提交入口失效反馈、补充新的爆料平台或社区，也可以就投稿方向进行咨询。我们通常在 2 至 3 个工作日内回复。',
    keywords: '联系爆料网上,反馈入口失效,补充爆料平台,投稿咨询,爆料网上',
    path: '/contact',
    jsonLd,
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('contact_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      setMessageLength(0);
      return;
    }

    formData.delete('contact_alt');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string' && value.trim() !== '') {
        payload.append(key, value);
      }
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = serverMessage.toLowerCase().includes('spam');
      const succeeded = response.ok && parsed?.code === 'OK' && !isSpam;

      if (succeeded) {
        setStatus('success');
        setFormError('');
        form.reset();
        setMessageLength(0);
      } else {
        setStatus('error');
        setFormError(serverMessage || '提交没有成功，请稍后重试，或直接发送邮件联系我们。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交没有成功，请稍后重试，或直接发送邮件联系我们。');
    }
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '联系我们' }]} />
          <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">联系我们</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            无论是在导航里发现了失效入口、想补充一个靠谱的渠道，还是不确定自己的情况该投到哪里，
            都可以在下面留言告诉我们。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.3fr_0.7fr]">
          <section aria-labelledby="contact-form-title">
            <h2 id="contact-form-title" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              给我们留言
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              带 <span className="text-primary-600">*</span> 的为必填项，详细说明请控制在 {MESSAGE_LIMIT} 字以内。
            </p>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
              noValidate
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block text-sm font-medium text-foreground-800">
                    称呼 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block text-sm font-medium text-foreground-800">
                    邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="contact-type" className="mb-2 block text-sm font-medium text-foreground-800">
                    反馈类型 <span className="text-primary-600">*</span>
                  </label>
                  <select
                    id="contact-type"
                    name="type"
                    required
                    defaultValue={feedbackTypes[0]}
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 cursor-pointer"
                  >
                    {feedbackTypes.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-target" className="mb-2 block text-sm font-medium text-foreground-800">
                    相关平台或类别
                  </label>
                  <input
                    id="contact-target"
                    name="target"
                    type="text"
                    placeholder="例如：消费维权在线，或选择跳过"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="contact-message" className="mb-2 block text-sm font-medium text-foreground-800">
                  详细说明 <span className="text-primary-600">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={6}
                  maxLength={MESSAGE_LIMIT}
                  onChange={(event) => setMessageLength(event.target.value.length)}
                  placeholder="请简要说明情况，涉及入口失效的可以附上相关页面名称。请勿在留言中填写身份证号、住址等敏感信息。"
                  className="w-full resize-y rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                ></textarea>
                <p className="mt-1.5 text-right text-xs text-foreground-400">
                  {messageLength} / {MESSAGE_LIMIT}
                </p>
              </div>

              <div className="legacy-contact-field" aria-hidden="true">
                <label htmlFor="contact-alt">请勿填写此栏</label>
                <input
                  id="contact-alt"
                  name="contact_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
                >
                  <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
                  {status === 'submitting' ? '正在提交…' : '提交留言'}
                </button>
                <p className="text-xs leading-relaxed text-foreground-500">
                  提交即表示你同意我们仅将该信息用于回复本次反馈。
                </p>
              </div>

              {status === 'success' && (
                <div className="mt-5 flex items-start gap-2.5 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-800">
                  <i className="ri-checkbox-circle-line mt-0.5"></i>
                  收到了，感谢你的反馈。我们会在 2 至 3 个工作日内回复到你填写的邮箱。
                </div>
              )}
              {status === 'error' && (
                <div className="mt-5 flex items-start gap-2.5 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-700">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  {formError}
                </div>
              )}
            </form>
          </section>

          <aside className="space-y-5">
            {infoCards.map((card) => (
              <div key={card.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${card.icon} text-lg`}></i>
                </span>
                <h2 className="mt-4 font-heading text-base font-semibold text-foreground-950">{card.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{card.desc}</p>
              </div>
            ))}

            <div className="rounded-lg border border-background-200 bg-primary-900 p-5">
              <h2 className="font-heading text-base font-semibold text-background-50">投稿前建议先看一眼</h2>
              <p className="mt-2 text-sm leading-relaxed text-background-200">
                材料怎么整理、怎么选渠道、如何保护自己，这三件事指南里都写清楚了。
              </p>
              <Link
                to="/guide"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400 cursor-pointer"
              >
                阅读发布指南<i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}